import { Contract, ObjectEntity, PageDto } from '@ekt-group/general-purpose-api-interfaces';
import { ContractsSideloading } from '@ekt-group/general-purpose-api-interfaces/src/modules/contracts/types/contracts.enum';
import { ObjectsSideloading } from '@ekt-group/general-purpose-api-interfaces/src/modules/objects/types/objects-sideloading.enum';
import { CustomerContractRole } from '@ekt-group/general-purpose-api-interfaces/src/types/customer-contract-role.enum';
import {
  ChevronDown,
  ChevronRight,
  File02,
  File03,
  FileCheck02,
  Hash02,
  Map01,
  Pencil02,
  Plus,
  Settings04,
  UserCheck01,
} from '@untitled-ui/icons-react';
import { Badge, Checkbox, Pagination, Skeleton } from 'antd';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import { useUser } from '@/features/auth';
import { useGetContracts } from '@/features/contracts/api/use-get-contracts';
import ContractViewModal from '@/features/contracts/components/ContractViewModal';
import DownloadContractPDFButton from '@/features/contracts/components/DownloadContractPDFButton';
import DownloadDischargeSchedulesPDFButton from '@/features/contracts/components/DownloadDischargeSchedulesPDFButton';
import DownloadSchedulesExcelButton from '@/features/contracts/components/DownloadSchedulesExcelButton';
import DownloadSignedContractButton from '@/features/contracts/components/DownloadSignedContractButton';
import { useGetFilters } from '@/features/desktop/api/use-get-filters';
import { useGetObjects } from '@/features/objects/api/use-get-objects';
import { getAllowedContractIdsFromPermissions } from '@/helpers/get-allowed-contract-ids-from-permissions';
import { safeNumber } from '@/helpers/safe-number';
import { useCollapsableList } from '@/hooks/use-collapsable-list';
import { useFilterParams } from '@/hooks/use-filter-params';
import { useUserInfoStore } from '@/store';
import { CONTRACT_MODE, useContractModalStore } from '@/store/use-contract-modal-store';
import { cn } from '@/utils/cn';

const ContractsPage = () => {
  const { data: user } = useUser();
  const { code: ownerCode, permissions } = useUserInfoStore((state) => state.selectedAccount);
  const { setMode } = useContractModalStore();

  const { t } = useTranslation('contractsPage');

  const [selectedContractId, setSelectedContractId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);

  const { filters, setFilterValue, clearAllFilters, hasFilters } = useFilterParams([
    'contractNumber',
    'garbageId',
    'itemTypeId',
    'showEndedContracts',
    'showEndedObjects',
  ]);

  const { pagination, expandedItemIds, toggleExpandItems, toggleExpandItem } = useCollapsableList<Contract>();
  const { page, pageSize, handlePaginationChange } = pagination;

  const contractsQuery = useGetContracts<PageDto<Contract>>({
    sideloading: [ContractsSideloading.Owner, ContractsSideloading.Kojv],
    ownerCodes: [ownerCode],
    contractNumbers: [filters.contractNumber],
    kojvIds: [safeNumber(filters.kojvId)],
    objectAddressIds: [safeNumber(filters.objectAddressId)],
    endDateFrom: filters.showEndedContracts === 'true' ? undefined : dayjs().startOf('day').toISOString(),
    page,
    pageSize,
    // @ts-ignore
    order: 'DESC',
    orderBy: 'endDate',
  });

  const objectsQuery = useGetObjects<ObjectEntity[]>({
    params: {
      objectAddressIds: [safeNumber(filters.objectAddressId)],
      disablePagination: true,
      contractNumbers: contractsQuery.data?.data?.map((contract) => contract.contractNumber),
      sideloading: [ObjectsSideloading.Contract, ObjectsSideloading.ObjectAddress],
      ownerCodes: [ownerCode],
      contractIds:
        user?.roles?.includes(CustomerContractRole.ChildContract) || user?.roles?.includes(CustomerContractRole.SharedObjectContract)
          ? getAllowedContractIdsFromPermissions(permissions)
          : undefined,
      isActive: filters.showEndedContracts === 'true' ? undefined : true,
    },
    queryConfig: {
      enabled: !!contractsQuery.data?.data?.length,
    },
  });

  const getObjectsForContract = (contractId: number): ObjectEntity[] => {
    if (!objectsQuery.data || !Array.isArray(objectsQuery.data)) {
      return [];
    }
    return objectsQuery.data.filter((objectEntity: ObjectEntity) => objectEntity.contract.id === contractId);
  };

  const filtersQuery = useGetFilters({
    userId: user.id,
    types: ['kojv', 'garbage', 'contracts', 'addresses'],
    ownerCodes: [ownerCode],
  });

  const toggleFiltersVisibility = () => {
    setIsFiltersVisible(!isFiltersVisible);
  };

  const handleEditButtonClick = (id: number) => {
    setSelectedContractId(id);
    setMode(CONTRACT_MODE.VIEW);
    setIsModalOpen(true);
  };

  const handleManageAttorneysButtonClick = (id: number) => {
    setSelectedContractId(id);
    setMode(CONTRACT_MODE.MANAGE_ATTORNEYS);
    setIsModalOpen(true);
  };

  const appliedFiltersCount = Object.values(filters)?.filter(Boolean).length;

  return (
    <div className="flex flex-col h-full gap-6 text-sm md:text-base pt-5 lg:px-8 md:px-6">
      <div className="flex flex-col gap-2 bg-white p-6 rounded-[32px]">
        <div key="desktop-filters" className="flex flex-col gap-3">
          <div className="flex sm:flex-row flex-col justify-between gap-3">
            <div className="flex items-center justify-between order-1 col-span-6 gap-4 sm:justify-start sm:col-span-3">
              <span className="sm:text-[32px] text-[24px] font-semibold">{t('contracts')}</span>
              <button onClick={toggleFiltersVisibility} className="flex items-center gap-1 text-[#A7AD2A] relative">
                <Settings04 className="w-4 h-4" />
                <span className="text-base font-medium">{t(isFiltersVisible ? 'closeFilters' : 'openFilters', { ns: 'common' })}</span>
                {hasFilters && <Badge count={appliedFiltersCount} showZero={false} color="#A7AD2A" />}
              </button>
            </div>
            <Button
              onClick={() => toggleExpandItems(contractsQuery.data?.data)}
              className="order-8 col-span-6 sm:justify-self-end sm:order-0 sm:order-2 sm:col-span-3 sm:w-auto w-full"
            >
              {expandedItemIds.length ? t('collapseAll', { ns: 'common' }) : t('expandAll', { ns: 'common' })}
            </Button>
          </div>
        </div>
        <div className={cn('flex gap-2 flex-col', { hidden: !isFiltersVisible })}>
          <div className="flex md:flex-row flex-col flex-wrap gap-2">
            <Select
              name="contractNumber"
              value={filters.contractNumber}
              onItemSelect={(value) => setFilterValue('contractNumber', value)}
              className="flex-1"
              placeholder={t('contractNumber', { ns: 'contractsTable' })}
              options={filtersQuery.data?.contracts?.map((el) => ({
                value: el.contractNumber,
                label: el.contractNumber,
              }))}
              leftSide={
                <span className="w-6 h-6 text-brand-brown">
                  <Hash02 />
                </span>
              }
            />
            <Select
              name="kojvId"
              value={filters.kojvId}
              onItemSelect={(value) => setFilterValue('kojvId', value)}
              className="flex-1"
              placeholder={t('kojvName', { ns: 'contractsTable' })}
              options={filtersQuery.data?.kojv?.map((el) => ({
                value: el.id.toString(),
                label: el.name,
              }))}
              leftSide={<File02 className="w-6 h-6 text-brand-brown" />}
            />
            <div className="flex flex-1 gap-2 sm:flex-row flex-col">
              <Select
                name="objectAddressId"
                value={filters.objectAddressId}
                onItemSelect={(value) => setFilterValue('objectAddressId', value)}
                className="flex-1"
                placeholder={t('objectAddress', { ns: 'serviceHistoryPage' })}
                options={filtersQuery.data?.addresses?.map((el) => ({
                  value: el.id.toString(),
                  label: el.address,
                }))}
                leftSide={<Map01 className="w-6 h-6 text-brand-brown" />}
              />
              <button
                onClick={clearAllFilters}
                className={cn(
                  'md:h-12 h-10 rounded-[8px] text-center col-span-1 sm:w-[85px] w-full justify-self-end items-center justify-center',
                  hasFilters ? 'bg-brand-green text-gray-10' : 'sm:flex hidden bg-[#F5F5F5] text-[#B9B9B9]',
                )}
              >
                {t('clearAll')}
              </button>
            </div>
          </div>
          <div className="flex sm:gap-4 gap-2 sm:flex-row flex-col sm:items-center">
            <Checkbox
              checked={filters.showEndedContracts === 'true'}
              onChange={(event) => setFilterValue('showEndedContracts', String(event.target.checked))}
            >
              {t('showEndedContracts')}
            </Checkbox>
            <Checkbox
              checked={filters.showEndedObjects === 'true'}
              onChange={(event) => setFilterValue('showEndedObjects', String(event.target.checked))}
            >
              {t('showEndedObjects', { ns: 'desktopPage' })}
            </Checkbox>
          </div>
        </div>
      </div>
      <div key="contracts-page-body" className="flex-1 pb-6 flex flex-col gap-6 items-end">
        {contractsQuery.isLoading ? (
          <Skeleton.Node className="w-full rounded-[32px] h-[104px]" active />
        ) : (
          contractsQuery.data?.data?.map((contract) => (
            <div
              key={`contract-${contract.id}`}
              className="w-full gap-2 grid lg:grid-cols-9 md:grid-cols-7 sm:grid-cols-5 grid-cols-3 bg-white rounded-[32px] border border-[#F0F0F0] p-3"
            >
              <div
                key={`contract-details-${contract.id}`}
                className="grid md:text-base sm:text-sm text-xs lg:grid-cols-9 md:grid-cols-7 sm:grid-cols-5 grid-cols-3 gap-5 gap-y-8 p-3 lg:col-span-9 md:col-span-7 sm:col-span-6 col-span-3"
              >
                <div key={`contract-number-section-${contract.id}`} className="flex gap-2 items-center sm:col-span-1 order-1">
                  <button
                    key={`expand-button-${contract.id}`}
                    onClick={() => toggleExpandItem(contract.id)}
                    className={cn('text-gray-7 outline-none', {
                      'opacity-0 pointer-events-none': !getObjectsForContract(contract.id)?.length,
                    })}
                  >
                    {expandedItemIds.includes(contract.id) ? (
                      <ChevronDown className="sm:w-6 sm:h-6 h-5 w-5" />
                    ) : (
                      <ChevronRight className="sm:w-6 sm:h-6 h-5 w-5" />
                    )}
                  </button>
                  <div key={`contract-number-display-${contract.id}`} className="flex flex-col gap-2">
                    <span className="text-gray-10 whitespace-nowrap font-medium leading-[24px]">
                      {t('number', { ns: 'contractsTable' })}
                    </span>
                    <span className="text-[#A7AD2A] underline font-normal">{contract.contractNumber}</span>
                  </div>
                </div>
                <div
                  key={`contract-name-section-${contract.id}`}
                  className="flex flex-col justify-center gap-2 col-span-2 sm:col-span-1 order-2 sm:items-start items-end"
                >
                  <span className="text-gray-10 whitespace-nowrap font-medium leading-[24px] sm:text-left text-right">
                    {t('contractName', { ns: 'objectsTable' })}
                  </span>
                  <span className="text-gray-10 font-normal sm:text-left text-right">#{contract.contractNumber}</span>
                </div>
                <div
                  key={`customer-name-section-${contract.id}`}
                  className={cn(
                    'flex lg:justify-self-end flex-col gap-2 col-span-1 md:col-span-5 lg:col-span-1 lg:order-3 md:order-8 sm:order-6 order-3',
                    {
                      'md:hidden lg:flex': !expandedItemIds.includes(contract.id),
                    },
                  )}
                >
                  <span className="text-gray-10 whitespace-nowrap font-medium leading-[24px]">
                    {t('customerName', { ns: 'contractsTable' })}
                  </span>
                  <span className="text-[#595959] font-normal">
                    {contract?.owner?.companyName || `${contract?.owner?.firstname} ${contract?.owner?.surname}`}
                  </span>
                </div>
                <div
                  key={`validity-period-section-${contract.id}`}
                  className={cn(
                    'flex whitespace-nowrap flex-col justify-center gap-2 md:col-span-1 col-span-2 lg:order-4 md:order-9 sm:order-7 order-4 sm:items-start items-end',
                    {
                      'md:hidden lg:flex': !expandedItemIds.includes(contract.id),
                    },
                  )}
                >
                  <span className="text-gray-10 font-medium leading-[24px]">{t('period', { ns: 'contractsTable' })}</span>
                  <span
                    className={cn(
                      'p-2 rounded-[8px] text-white text-sm font-normal',
                      dayjs(contract.endDate).isBefore(dayjs(), 'day') ? 'bg-[#B9B9B9]' : 'bg-brand-green',
                    )}
                  >
                    {dayjs(contract?.startDate).format('DD.MM.YY')} - {dayjs(contract?.endDate).format('DD.MM.YY')}
                  </span>
                </div>
                <div
                  key={`discharge-schedules-section-${contract.id}`}
                  className="flex flex-col justify-center gap-2 col-span-1 lg:order-5 md:order-3 sm:order-3 order-5"
                >
                  <span className="text-gray-10 font-medium leading-[24px]">{t('dischargesSchedules', { ns: 'objectItemsTable' })}</span>
                  <div className="flex gap-2">
                    <DownloadDischargeSchedulesPDFButton contractIds={[contract.id]} />
                    <DownloadSchedulesExcelButton contractIds={[contract.id]} />
                  </div>
                </div>
                <div
                  key={`contact-details-section-${contract.id}`}
                  className="flex flex-col justify-center gap-2 sm:col-span-1 col-span-2 lg:order-6 md:order-4 sm:order-4 order-6 sm:items-start items-end"
                >
                  <span className="text-gray-10 font-medium leading-[24px]">
                    <Pencil02 className="w-5 h-5" />
                  </span>
                  <button
                    onClick={() => handleEditButtonClick(contract.id)}
                    className="text-brand-brown sm:text-left text-right underline font-normal"
                  >
                    {t('contactDetails', { ns: 'contractsTable' })}
                  </button>
                </div>
                <div
                  key={`manage-users-section-${contract.id}`}
                  className="flex flex-col justify-center gap-2 col-span-1 lg:order-7 md:order-5 sm:order-5 order-7 items-start"
                >
                  <span className="text-gray-10 font-medium leading-[24px]">
                    <UserCheck01 className="w-5 h-5" />
                  </span>
                  <button
                    onClick={() => handleManageAttorneysButtonClick(contract.id)}
                    className="text-brand-brown text-left underline font-normal"
                  >
                    {t('manageUsers', { ns: 'contractsTable' })}
                  </button>
                </div>
                <div
                  key={`invoices-section-${contract.id}`}
                  className="flex flex-col justify-center sm:items-start items-center gap-2 col-span-1 lg:order-8 md:order-6 sm:order-8 order-8"
                >
                  <span className="text-gray-10 font-medium leading-[24px]">
                    <File03 className="w-5 h-5" />
                  </span>
                  <Link to={`/invoices?contractNumber=${contract.contractNumber}`}>
                    <span className="text-brand-brown underline font-normal text-center sm:text-left">
                      {t('viewInvoices', { ns: 'contractsTable' })}
                    </span>
                  </Link>
                </div>
                <div
                  key={`sign-contract-section-${contract.id}`}
                  className="flex flex-col justify-center gap-2 col-span-1 lg:order-9 md:order-7 sm:order-9 order-9 sm:items-start items-end"
                >
                  {contract.signedStatus !== 0 ? (
                    <DownloadSignedContractButton key={`download-signed-contract-${contract.id}`} contractId={contract.id} />
                  ) : (
                    <>
                      <div className="flex gap-2">
                        <span
                          className={cn('text-gray-10 font-medium leading-[24px]', {
                            hidden: dayjs(contract.endDate).isBefore(dayjs(), 'day'),
                          })}
                        >
                          <FileCheck02 className="w-5 h-5" />
                        </span>
                        <DownloadContractPDFButton contractId={contract.id} />
                      </div>
                      <Link
                        to={`/contracts/${contract.id}/sign`}
                        className={cn('text-brand-brown underline font-normal sm:text-left text-right', {
                          hidden: dayjs(contract.endDate).isBefore(dayjs(), 'day'),
                        })}
                      >
                        {t('signContract', { ns: 'contractsTable' })}
                      </Link>
                    </>
                  )}
                </div>
              </div>
              {expandedItemIds.includes(contract.id) &&
                (objectsQuery.data as ObjectEntity[])
                  ?.filter((objectEntity) => objectEntity.contract.id === contract.id)
                  ?.map((objectEntity) => (
                    <div
                      key={`object-item-${objectEntity.id}`}
                      className="lg:col-span-9 md:col-span-7 sm:col-span-5 col-span-3 grid lg:grid-cols-9 md:grid-cols-7 sm:grid-cols-5 grid-cols-3 gap-3 items-center rounded-[20px] py-4 px-3 bg-brand-green-light"
                    >
                      <div
                        key={`object-address-${objectEntity.id}`}
                        className="sm:col-span-2 sm:items-center items-start lg:col-span-4 col-span-3 flex sm:flex-row flex-col gap-2"
                      >
                        <p className="text-gray-10 font-medium sm:text-lg text-sm">
                          {objectEntity.objectAddress?.address || objectEntity.fullAddress}
                        </p>
                      </div>
                      <div key={`object-discharge-schedules-${objectEntity.id}`} className="col-span-1 flex flex-col gap-2">
                        <span className="text-gray-10 text-sm sm:text-base font-medium leading-[24px]">
                          {t('dischargeSchedules', { ns: 'contractsTable' })}
                        </span>
                        <div className="flex gap-2">
                          <DownloadDischargeSchedulesPDFButton objectIds={[objectEntity.id]} />
                          <DownloadSchedulesExcelButton objectIds={[objectEntity.id]} />
                        </div>
                      </div>
                      <div key={`object-actions-${objectEntity.id}`} className="col-span-2 md:col-span-4 flex flex-col gap-2 items-end">
                        <span className="text-sm text-[#A7AD2A]">{objectEntity.id}</span>
                        <Link
                          to={`/?objectAddressId=${objectEntity.objectAddress?.id}&expanded=${objectEntity.id}`}
                          className="inline-flex"
                        >
                          <Button variant="primaryAlt" className="min-w-[176px]">
                            <Plus width={16} height={16} />
                            {t('goToObject', { ns: 'contractsTable' })}
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ))}
            </div>
          ))
        )}
        <Pagination
          hideOnSinglePage
          key="pagination"
          current={page}
          pageSize={pageSize}
          total={contractsQuery.data?.meta?.totalItems}
          onChange={handlePaginationChange}
          showSizeChanger
          pageSizeOptions={['10', '20', '50', '100']}
        />
        {isModalOpen && (
          <ContractViewModal
            key="contract-view-modal"
            contractId={selectedContractId}
            isModalOpen={isModalOpen}
            handleClose={() => {
              setIsModalOpen(false);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ContractsPage;
